import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useController } from 'react-hook-form';

import styles from './FormCheckbox.module.css';
import { DropdownOptions, TooltipObject } from '../../models/formModel';
import Popup from '../popup/popup';

interface FormCheckboxProps {
  id: string;
  label?: string | ReactNode;
  required?: boolean;
  hasNumber?: boolean;
  options?: DropdownOptions[];
  /** The number of the items can be selected
   *
   * Set as **0** to allow infinite selection
   *
   * **_Default: 1_**
   */
  selectionCount?: number;
  hideLabel?: boolean;
  centerIfSingle?: boolean;
  tooltip?: TooltipObject;
  valueChanged?: (_: string) => void;
}

interface CheckedItems {
  [key: string]: boolean;
}

const FormCheckbox: React.FC<FormCheckboxProps> = ({
  id,
  label = '',
  hasNumber = false,
  required = false,
  options = [],
  selectionCount = 1,
  hideLabel = false,
  centerIfSingle = true,
  tooltip,
  valueChanged = (_: string) => {},
}) => {
  const [checkboxItem, setCheckboxItem] = useState<CheckedItems>({});
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const {
    field: { onChange: inputCheckChanged, value },
  } = useController({ name: id, rules: { required: required } });

  useEffect(() => {
    const newObj: CheckedItems = {};
    options.forEach((_: DropdownOptions, index: number) => {
      newObj[`${id}_checkbox_${index}`] = false;
    });

    setCheckboxItem(newObj);
  }, []);

  useEffect(() => {
    const allCheckedItems = Object.keys(checkboxItem)
      .map((c: string) => (checkboxItem[c] ? c : null))
      .filter((r) => r)
      .map((final) => +final?.split('_').at(-1)!);

    const allValue = allCheckedItems.map((v) => options[v].value);

    inputCheckChanged(allValue.join(','));
    valueChanged(allValue.join(','));
  }, [checkboxItem]);

  useEffect(() => {
    if (!value) return;

    const values = (value as string).split(','); // since you're storing CSV
    const newObj: CheckedItems = {};

    options.forEach((opt, index) => {
      const checkId = `${id}_checkbox_${index}`;
      newObj[checkId] = values.includes(opt.value.toString());
    });

    setCheckboxItem(newObj);
  }, [value]);

  const checkChanged = (checkId: string, checked: boolean) => {
    const newObj = JSON.parse(JSON.stringify(checkboxItem));

    const allCheckedItems = Object.keys(checkboxItem).filter(
      (c: string) => checkboxItem[c]
    );

    if (checked && selectionCount > 0) {
      if (allCheckedItems.length >= selectionCount) {
        newObj[allCheckedItems[0]] = false;
      }
    }

    newObj[checkId] = checked;
    setCheckboxItem(newObj);
  };

  return (
    <>
      <div className={styles.formInputContainer}>
        {!hideLabel && (
          <div className={'labelContainer'}>
            <div className={required ? 'requiredLabel' : ''}>{label}</div>
            {tooltip && (
              <img
                className={'tooltipIcon'}
                src="/images/info.png"
                alt=""
                onClick={() => setShowPopup(true)}
              />
            )}
          </div>
        )}
        <div
          className={styles.checkboxContainer}
          // style={{
          //   gridTemplateColumns: `repeat(${
          //     options.length > 5 ? 5 : options.length
          //   }, 1fr)`,
          //   alignSelf:
          //     options.length == 1 && centerIfSingle ? 'center' : 'flex-start',
          // }}
        >
          {options.map((opt: DropdownOptions, index: number) => {
            const checkId = `${id}_checkbox_${index}`;
            return (
              <div className={styles.checkboxItem} key={checkId}>
                <input
                  id={checkId}
                  type="checkbox"
                  checked={checkboxItem[checkId]}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    checkChanged(checkId, e.target.checked);
                  }}
                />
                {opt.label && (
                  <label htmlFor={checkId} className={styles.checkboxLabel}>
                    {hasNumber ? index + 1 : ''} {opt.label}{' '}
                    {checkboxItem[checkId]}
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Popup
        show={showPopup}
        title={tooltip?.title}
        onButtonClicked={() => setShowPopup(false)}
      >
        {tooltip?.content}
      </Popup>
    </>
  );
};

export default FormCheckbox;
